<template>
  <div class="case-page">
    <banner title="经典案例" subtitle="PROJECT SHOWCASE" />

    <section class="case-section" aria-labelledby="case-heading">
      <div class="case-section__inner" v-loading="loading">
        <header class="case-intro" v-reveal>
          <div>
            <span class="eyebrow">PROJECT PRACTICE</span>
            <h2 id="case-heading">用真实项目沉淀可复制的管理经验</h2>
          </div>
          <p>从项目过程管理到成果交付，持续呈现平台在真实业务场景中的应用价值。</p>
        </header>

        <div v-if="caseList.length" class="case-grid">
          <article class="case-card" v-for="(cas, index) in caseList" :key="cas.Id || cas.Title" v-reveal="(index % 2) * 90">
            <router-link
              class="case-card__media"
              :to="{ name: 'casedetails', params: { id: cas.Id }}"
              :aria-label="`查看案例：${cas.Title || '案例详情'}`"
            >
              <img v-lazy="imgserver + cas.Img" :alt="cas.Title || '经典案例'" />
              <span class="case-card__index">CASE</span>
            </router-link>
            <div class="case-card__body">
              <h3>{{ cas.Title || "案例项目" }}</h3>
              <p>{{ cas.Content || "查看项目背景、实施过程与交付成果。" }}</p>
              <router-link
                class="case-card__more"
                :to="{ name: 'casedetails', params: { id: cas.Id }}"
              >
                查看项目详情
                <span aria-hidden="true">→</span>
              </router-link>
            </div>
          </article>
        </div>

        <div v-else-if="!loading" class="case-empty" role="status">
          <i class="el-icon-folder-opened" aria-hidden="true"></i>
          <h3>{{ errorMessage ? "案例加载失败" : "案例正在整理中" }}</h3>
          <p>{{ errorMessage || "更多项目实践将陆续发布，敬请关注。" }}</p>
          <el-button v-if="errorMessage" type="primary" plain @click="loadCases">重新加载</el-button>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import Banner from "../components/Banner";

export default {
  components: { Banner },
  data() {
    return {
      loading: true,
      errorMessage: "",
      caseList: []
    };
  },
  mounted() {
    this.loadCases();
  },
  methods: {
    loadCases() {
      this.loading = true;
      this.errorMessage = "";
      this.$http
        .get("Cases/GetCasesAll")
        .then(response => {
          this.caseList = Array.isArray(response.data) ? response.data : [];
        })
        .catch(error => {
          this.caseList = [];
          this.errorMessage = "暂时无法获取案例数据，请稍后重试。";
          window.console.warn("Failed to load cases", error);
        })
        .then(() => {
          this.loading = false;
        });
    }
  }
};
</script>

<style lang="scss" scoped>
.case-page {
  min-height: 100vh;
  background:
    radial-gradient(circle at 10% 20%, rgba(18, 112, 180, 0.08), transparent 28rem),
    var(--color-surface-soft);
}

.case-section {
  padding: var(--section-space) var(--page-gutter);
}

.case-section__inner {
  width: min(100%, var(--content-width));
  min-height: 320px;
  margin: 0 auto;
}

.case-intro {
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(260px, 0.8fr);
  align-items: end;
  gap: 48px;
  margin-bottom: clamp(36px, 5vw, 64px);

  .eyebrow {
    display: inline-flex;
    margin-bottom: 14px;
    color: var(--color-brand);
    font-size: var(--text-eyebrow);
    font-weight: 700;
    letter-spacing: var(--tracking-eyebrow);
  }

  h2 {
    max-width: 720px;
    margin: 0;
    color: var(--color-ink);
    font-size: var(--text-h2);
    font-weight: 700;
    line-height: 1.2;
    letter-spacing: var(--tracking-cjk);
  }

  > p {
    margin: 0;
    color: var(--color-ink-muted);
    font-size: 16px;
    line-height: 1.9;
  }
}

.case-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: clamp(22px, 3vw, 36px);
}

.case-card {
  overflow: hidden;
  background: #fff;
  border: 1px solid rgba(15, 50, 79, 0.08);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  transition: transform var(--duration-base) var(--ease-out), box-shadow var(--duration-base) ease, border-color var(--duration-base) ease;

  &:hover {
    transform: translateY(var(--hover-lift));
    border-color: rgba(18, 112, 180, 0.2);
    box-shadow: var(--shadow-lg);
  }
}

.case-card__media {
  position: relative;
  display: block;
  aspect-ratio: 16 / 10;
  overflow: hidden;
  background: #dfe9f1;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, transparent 46%, rgba(7, 35, 57, 0.55));
    pointer-events: none;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.45s ease;
  }

  &:hover img {
    transform: scale(1.04);
  }
}

.case-card__index {
  position: absolute;
  z-index: 1;
  right: 20px;
  bottom: 18px;
  color: rgba(255, 255, 255, 0.88);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: var(--tracking-eyebrow);
}

.case-card__body {
  padding: clamp(24px, 3vw, 34px);

  h3 {
    margin: 0 0 12px;
    color: var(--color-ink);
    font-size: clamp(20px, 2vw, 25px);
    line-height: 1.45;
  }

  p {
    display: -webkit-box;
    min-height: 3.6em;
    margin: 0 0 24px;
    overflow: hidden;
    color: var(--color-ink-muted);
    line-height: 1.8;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
}

.case-card__more {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  color: var(--color-brand);
  font-weight: 700;
  text-decoration: none;

  span {
    transition: transform 0.2s ease;
  }

  &:hover span {
    transform: translateX(4px);
  }
}

.case-empty {
  display: grid;
  place-items: center;
  min-height: 320px;
  padding: 48px 20px;
  text-align: center;
  background: #fff;
  border: 1px dashed rgba(18, 112, 180, 0.28);
  border-radius: var(--radius-lg);

  i {
    margin-bottom: 16px;
    color: var(--color-brand);
    font-size: 48px;
  }

  h3 {
    margin: 0 0 8px;
    color: var(--color-ink);
  }

  p {
    margin: 0 0 20px;
    color: var(--color-ink-muted);
  }
}

@media (max-width: 768px) {
  .case-section {
    padding-top: 48px;
  }

  .case-intro {
    grid-template-columns: 1fr;
    gap: 18px;
  }

  .case-grid {
    grid-template-columns: 1fr;
  }

  .case-card__body {
    padding: 24px 20px 26px;
  }
}
</style>
